import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, Ban, ShieldCheck, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/umami";

interface MockMessage {
  from: string;
  subject: string;
  to: string;
  time: string;
}

/** Index of the example message that shows the unfamiliar-sender flag. */
const LEAK_INDEX = 2;

/**
 * Hero: white text on a solid navy field (high contrast), with the Deep water
 * mesh glowing only on the right, behind the product mock.
 */
export function Hero() {
  const { t } = useTranslation("landing");
  const messages = t("hero.mock.messages", { returnObjects: true }) as MockMessage[];

  return (
    <section className="relative overflow-hidden pb-16 md:pb-24">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[min(100%,1100px)] bg-mesh-low clip-skew md:h-[820px] md:bg-mesh"
      />

      <div className="relative mx-auto grid max-w-[1200px] items-start gap-14 px-5 pt-32 md:px-10 md:pt-40 lg:grid-cols-[1.15fr_0.85fr] lg:gap-24">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[13px] font-semibold text-white">
            <span className="size-1.5 rounded-full bg-[#7cf5d0]" />
            {t("hero.badge")}
          </span>
          <h1 className="mt-6 text-[44px] leading-[1.02] font-bold tracking-[-0.045em] text-balance text-white sm:text-6xl lg:text-[64px] lg:leading-none xl:text-[76px]">
            {/* One sentence per line, so a short title never breaks mid-thought. */}
            {t("hero.title")
              .split(/(?<=[.!?])\s+/)
              .map((sentence, i) => (
                <span key={sentence} className="block">
                  {i > 0 ? " " : null}
                  {sentence}
                </span>
              ))}
          </h1>
          <p className="mt-6 max-w-[520px] text-lg leading-relaxed text-[#d5deea] md:text-[19px]">
            {t("hero.subheadline")}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg" className="shadow-primary">
              <Link
                to="/signup"
                onClick={() => trackEvent("signup_cta_click", { location: "hero" })}
              >
                {t("hero.primaryCTA")}
                <ArrowRight />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="ghost"
              className="text-white ring-1 ring-white/35 ring-inset hover:bg-white/10 hover:text-white"
            >
              <a href="#how">{t("hero.secondaryCTA")}</a>
            </Button>
          </div>
          <a
            href="#free"
            className="mt-5 inline-flex items-center gap-1.5 text-[15px] text-[#d5deea] transition-colors hover:text-white"
          >
            {t("hero.noDomain")}
            <ArrowRight className="size-4" />
          </a>
        </div>

        <div className="relative mx-auto w-full max-w-[600px] lg:mt-6">
          {/* Illustration: fixed light palette in both themes, like a screenshot. */}
          <div className="overflow-hidden rounded-2xl bg-white text-[#0a2540] shadow-float">
            <div className="flex items-center justify-between border-b border-[#e6ebf1] px-5 py-3.5">
              <span className="font-semibold">{t("hero.mock.inboxTitle")}</span>
              <span className="font-mono text-xs text-[#425466]">{t("hero.mock.domain")}</span>
            </div>
            {messages.map((m, i) => {
              const leak = i === LEAK_INDEX;
              return (
                <div
                  key={m.subject}
                  className={`grid grid-cols-[8px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3.5 sm:grid-cols-[8px_120px_minmax(0,1fr)_auto] sm:px-5 ${
                    i ? "border-t border-[#e6ebf1]" : ""
                  } ${leak ? "bg-[#fff8ec]" : ""}`}
                >
                  <span
                    className={`size-2 rounded-full ${i <= LEAK_INDEX ? "bg-[#2e6bff]" : ""}`}
                  />
                  <span className="hidden truncate text-sm font-semibold sm:block">{m.from}</span>
                  <div className="min-w-0">
                    <div className="truncate text-sm">
                      <span className="font-semibold sm:hidden">{m.from} · </span>
                      {m.subject}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <span className="rounded-md border border-[#e6ebf1] bg-[#f6f9fc] px-1.5 py-px font-mono text-[11.5px] text-[#425466]">
                        {m.to}@
                      </span>
                      {leak && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-[#fcedd4] px-1.5 py-px text-[11.5px] font-semibold whitespace-nowrap text-[#9a4d00]">
                          <TriangleAlert className="size-3" />
                          {t("hero.mock.unfamiliar")}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-right text-xs text-[#6b7c93]">{m.time}</span>
                </div>
              );
            })}
          </div>

          <div className="absolute -top-7 right-3 flex items-center gap-2.5 rounded-xl bg-white px-4 py-3 text-[#0a2540] shadow-float sm:-top-10 sm:-right-6">
            <span className="flex size-8 items-center justify-center rounded-lg bg-[#dcf5ef]">
              <ShieldCheck className="size-[17px] text-[#00806a]" />
            </span>
            <div>
              <div className="text-sm font-semibold">{t("hero.mock.trackersTitle")}</div>
              <div className="text-xs text-[#425466]">{t("hero.mock.trackersFrom")}</div>
            </div>
          </div>

          <div className="relative mx-3 -mt-6 rounded-xl bg-white p-4 text-[#0a2540] shadow-float sm:absolute sm:-bottom-14 sm:-left-14 sm:mx-0 sm:mt-0 sm:w-[290px] lg:-left-10">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-[#9a4d00]">
              <TriangleAlert className="size-4" />
              {t("hero.mock.leakTitle")}
            </div>
            <p className="mt-1.5 text-sm">
              <span className="font-mono">shop-2025@</span> {t("hero.mock.leakBody")}
            </p>
            <span className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full bg-[#0a1a33] px-3 text-[12.5px] font-semibold text-white">
              <Ban className="size-3.5" />
              {t("hero.mock.leakAction")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
